import React, { Suspense } from 'react';
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useLocation,
} from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import LoadingOverlay from './components/common/LoadingOverlay';
import SEO from './components/common/SEO/SEO';
import { getPrivatePageSeo } from './components/common/SEO/seoHelpers';
import SignIn from './components/SignIn/SignIn';
import SignUp from './components/SignUp/SignUp';
import CreateBusiness from './components/CreateBusiness/CreateBusiness';
import ScrollToTop from './components/ScrollToTop/ScrollToTop';
import AccountPage from './components/AccountPage/AccountPage';
import BusinessDashboard from './components/BusinessDashboard/BusinessDashboard';
import PartnershipAgreementDashboard from './components/PartnershipAgreementDashboard/PartnershipAgreementDashboard';
import OurPartners from './components/OurPartners/OurPartners';
import { AboutUs } from './components/AboutUs/AboutUs';
import ContactUsForm from './components/ContactUsForm/ContactUsForm';
import StoreFrontPage from './components/StoreFrontPage/StoreFrontPage';
import { ServiceDashboard } from './components/ServiceDashboard/ServiceDashboard';
import EmailVerificationPage from './components/EmailVerificationPage/EmailVerificationPage';
import { ForgotPasswordForm } from './components/ForgotPassword/ForgotPasswordForm';
import { ResetPasswordForm } from './components/ResetPasswordForm/ResetPasswordForm';
import { ChangePasswordForm } from './components/ChangePasswordForm/ChangePasswordForm';
import RefinedNavBar from './components/RefinedNavBar/RefinedNavBar';
import ServiceSummary from './components/ServiceSummary/ServiceSummary';
import Footer from './components/Footer/Footer';
import ServiceFinderAI from './components/ServiceFinderAI/ServiceFinderAI';
import './App.css';
import { Chatbot } from './components/Chatbot/Chatbot';
import Recommendations from './components/Recommendations/Recommendations';
import LegalPage from './components/LegalPage/LegalPage';
import ServiceSearchFlow from './components/ServiceSearchFlow/ServiceSearchFlow';
import ServiceProviders from './components/ServiceProviders/Serviceproviders';
import WizardSessionLinker from './components/WizardSessionLinker/WizardSessionLinker';
import ProviderSignup from './components/ProviderSignup/ProviderSignup';
import JoinExistingBusiness from './components/JoinExistingBusiness/JoinExistingBusiness';
import RequireAuth from './components/auth/RequireAuth';
import { LaunchAwareHome, LaunchAwareLaunchPage } from './utils/launchRoutes';
import RequireAdmin from './components/auth/RequireAdmin';
import LaunchModeGate from './components/auth/LaunchModeGate';
import LaunchNavBar from './components/LaunchNavBar/LaunchNavBar';
import AdminPage from './components/AdminPage/AdminPage';
import { trackEvent } from './services/api/analyticsService';
import { ThemeProvider } from '@mui/material/styles';
import { appTheme } from './theme/appTheme';
import { isLaunchMode, isLaunchModeLaunchNavPath } from './utils/launchMode';
import { getLoginReturnPathFromLocation } from './utils/authSessionExpired';
import EmailVerificationRequiredModal from './components/common/EmailVerificationRequiredModal/EmailVerificationRequiredModal';
import PartnershipAgreementRequiredModal from './components/common/PartnershipAgreementRequiredModal/PartnershipAgreementRequiredModal';
import ServicesNotConfiguredModal from './components/common/ServicesNotConfiguredModal/ServicesNotConfiguredModal';
import { businessService } from './services/api/businessService';
import NotFoundPage from './components/NotFoundPage/NotFoundPage';
import PaymentInvoices from './components/PaymentInvoices/PaymentInvoices';
import ServiceRequests from './components/ServiceRequests/ServiceRequests';
import SmsNotifications from './components/SmsNotifications/SmsNotifications';
import SiteTicker from './components/common/SiteTicker/SiteTicker';


const PaymentMethods = React.lazy(
  () => import('./components/PaymentMethods/PaymentMethods')
);
const AddPaymentMethod = React.lazy(
  () => import('./components/AddPaymentMethod/AddPaymentMethod')
);

const SESSION_STORAGE_KEY = 'nky_connect_app_session_sent';
const AppContent = () => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const isOnEmailVerificationLinkPage = location.pathname === '/verify-email';
  const isOnPartnershipAgreementPage =
    location.pathname === '/dashboard/partnership-agreement';
  const isOnServiceCatalogPage = location.pathname === '/dashboard/service-catalog';
  // Provider signup has its own embedded Step 4 (Agreement) — the app-wide gate
  // must not fight with it, and a fresh business always has zero Services, so
  // the "add a service" reminder shouldn't fire there either.
  const isOnProviderSignupPage = location.pathname === '/provider-signup';
  const showEmailVerificationModal =
    Boolean(user) &&
    user.ValidEmail === false &&
    !isOnEmailVerificationLinkPage;
  const privateSeo = getPrivatePageSeo(location.pathname);

  const [business, setBusiness] = React.useState(null);
  const [businessLoading, setBusinessLoading] = React.useState(false);
  const [servicesReminderDismissed, setServicesReminderDismissed] = React.useState(false);

  React.useEffect(() => {
    setServicesReminderDismissed(false);
    if (user?.Role === 'business_user' && user?.BusinessId) {
      let cancelled = false;
      setBusinessLoading(true);
      businessService
        .getBusinessForOwnerDashboard(user.BusinessId)
        .then((biz) => {
          if (!cancelled) setBusiness(biz);
        })
        .catch((err) => {
          console.error('Failed to load business info:', err);
        })
        .finally(() => {
          if (!cancelled) setBusinessLoading(false);
        });
      return () => {
        cancelled = true;
      };
    }
    setBusiness(null);
  }, [user?.Role, user?.BusinessId]);

  // The pages below can change the fields these popups gate on (signing the
  // agreement, adding a service). Re-fetch business info on the way out so a
  // stale `business` doesn't immediately re-trigger the popup we just resolved.
  const staleOnExitPaths = React.useMemo(
    () => [
      '/dashboard/partnership-agreement',
      '/dashboard/service-catalog',
      '/provider-signup',
    ],
    [],
  );
  const prevPathRef = React.useRef(location.pathname);
  React.useEffect(() => {
    const prevPath = prevPathRef.current;
    prevPathRef.current = location.pathname;
    if (
      prevPath !== location.pathname &&
      staleOnExitPaths.includes(prevPath) &&
      user?.Role === 'business_user' &&
      user?.BusinessId
    ) {
      // Hold the gate closed until the refresh lands; otherwise a stale
      // unsigned `business` can briefly reopen PartnershipAgreementRequiredModal.
      let cancelled = false;
      setBusinessLoading(true);
      businessService
        .getBusinessForOwnerDashboard(user.BusinessId)
        .then((biz) => {
          if (!cancelled) setBusiness(biz);
        })
        .catch((err) => console.error('Failed to refresh business info:', err))
        .finally(() => {
          if (!cancelled) setBusinessLoading(false);
        });
      return () => {
        cancelled = true;
      };
    }
  }, [location.pathname, staleOnExitPaths, user?.Role, user?.BusinessId]);

  const isBusinessUser = user?.Role === 'business_user' && Boolean(user?.BusinessId);
  const partnershipAgreementSigned = Boolean(business?.PartnershipAgreementAcceptedAt);
  const showPartnershipAgreementModal =
    !showEmailVerificationModal &&
    !isOnPartnershipAgreementPage &&
    !isOnProviderSignupPage &&
    isBusinessUser &&
    !businessLoading &&
    Boolean(business) &&
    !partnershipAgreementSigned;
  const showServicesReminderModal =
    !showEmailVerificationModal &&
    !isOnPartnershipAgreementPage &&
    !isOnServiceCatalogPage &&
    !isOnProviderSignupPage &&
    isBusinessUser &&
    !businessLoading &&
    Boolean(business) &&
    partnershipAgreementSigned &&
    (business.Services ?? []).length === 0 &&
    !servicesReminderDismissed;
  const fallbackSentRef = React.useRef(false);
  React.useEffect(() => {
    if (!loading) {
      try {
        if (!sessionStorage.getItem(SESSION_STORAGE_KEY)) {
          sessionStorage.setItem(SESSION_STORAGE_KEY, '1');
          trackEvent({ eventType: 'app_session', businessId: 'app' });
        }
      } catch {
        // sessionStorage unavailable (e.g. private mode); send once per load
        if (!fallbackSentRef.current) {
          fallbackSentRef.current = true;
          trackEvent({ eventType: 'app_session', businessId: 'app' });
        }
      }
    }
  }, [loading]);

  if (loading) {
    return <LoadingOverlay />;
  }

  return (
    <ScrollToTop>
      {privateSeo && (
        <SEO
          title={privateSeo.title}
          description="NKY Connect account and business tools."
          path={privateSeo.path}
          noindex
        />
      )}
      <EmailVerificationRequiredModal open={showEmailVerificationModal} />
      <PartnershipAgreementRequiredModal open={showPartnershipAgreementModal} />
      <ServicesNotConfiguredModal
        open={showServicesReminderModal}
        onClose={() => setServicesReminderDismissed(true)}
      />
      <WizardSessionLinker />
      <SiteTicker />
      {isLaunchMode() && isLaunchModeLaunchNavPath(location.pathname) ? (
        <LaunchNavBar />
      ) : (
        <>
          <RefinedNavBar />
          <div className="navbar-separator" />
        </>
      )}

      <div className="global-container">
        <LaunchModeGate>
          <Routes>
            <Route path="/" element={<LaunchAwareHome />} />
            <Route path="/launch" element={<LaunchAwareLaunchPage />} />
            <Route
              path="/login"
              element={
                user ? (
                  <Navigate
                    to={getLoginReturnPathFromLocation(location)}
                    replace
                  />
                ) : (
                  <SignIn />
                )
              }
            />
            <Route
              path="/sign-up"
              element={
                user ? (
                  <Navigate
                    to={getLoginReturnPathFromLocation(location)}
                    replace
                  />
                ) : (
                  <SignUp />
                )
              }
            />
            <Route path="/provider-signup" element={<ProviderSignup />} />
            <Route
              path="/join-existing-business"
              element={<JoinExistingBusiness />}
            />
            <Route path="/create-business" element={<CreateBusiness />} />
            <Route path="/terms" element={<LegalPage documentSlug="terms" />} />
            <Route
              path="/business-terms"
              element={<LegalPage documentSlug="business-terms" />}
            />
            <Route
              path="/privacy"
              element={<LegalPage documentSlug="privacy" />}
            />
            <Route
              path="/sms-terms"
              element={<LegalPage documentSlug="sms-terms" />}
            />
            <Route path="/services" element={<ServiceSummary />} />
            <Route path="/about" element={<AboutUs />} />
            <Route path="/contact" element={<ContactUsForm />} />
            <Route path="/partners" element={<OurPartners />} />

            {/* ─── Payment Methods (admin only — incomplete feature) ─── */}
            <Route
              path="/payment-methods"
              element={
                <RequireAuth>
                    <Suspense fallback={<LoadingOverlay />}>
                      <PaymentMethods />
                    </Suspense>
                </RequireAuth>
              }
            />
            <Route
              path="/add-payment-method"
              element={
                <RequireAuth>
                    <Suspense fallback={<LoadingOverlay />}>
                      <AddPaymentMethod />
                    </Suspense>
                </RequireAuth>
              }
            />
            <Route
              path="/invoices"
              element={
                <RequireAuth>
                    <Suspense fallback={<LoadingOverlay />}>
                      <PaymentInvoices />
                    </Suspense>
                </RequireAuth>
              }
            />

            {/* Service search: entry, then task and timing per trade */}
            <Route path="/find-a-pro" element={<ServiceSearchFlow />} />
            <Route path="/plumbing" element={<ServiceSearchFlow serviceType="plumbing" />} />
            <Route path="/hvac" element={<ServiceSearchFlow serviceType="hvac" />} />
            <Route path="/electrical" element={<ServiceSearchFlow serviceType="electrical" />} />
            <Route path="/landscaping" element={<ServiceSearchFlow serviceType="landscaping" />} />
            {/* Provider Routes (Search Results) */}
            <Route
              path="/plumbing/providers"
              element={<ServiceProviders serviceType="plumbing" />}
            />
            <Route
              path="/hvac/providers"
              element={<ServiceProviders serviceType="hvac" />}
            />
            <Route
              path="/electrical/providers"
              element={<ServiceProviders serviceType="electrical" />}
            />
            <Route
              path="/landscaping/providers"
              element={<ServiceProviders serviceType="landscaping" />}
            />
            <Route path="/ai-service-search" element={<ServiceFinderAI />} />
            <Route
              path="/account"
              element={
                <RequireAuth>
                  <AccountPage />
                </RequireAuth>
              }
            />
            <Route
              path="/admin"
              element={
                <RequireAuth>
                  <RequireAdmin>
                    <AdminPage />
                  </RequireAdmin>
                </RequireAuth>
              }
            />
            <Route
              path="/account/change-password"
              element={
                <RequireAuth>
                  <ChangePasswordForm />
                </RequireAuth>
              }
            />
            <Route
              path="/dashboard"
              element={
                <RequireAuth>
                  <BusinessDashboard />
                </RequireAuth>
              }
            />
            <Route
              path="/dashboard/partnership-agreement"
              element={
                <RequireAuth>
                  <PartnershipAgreementDashboard />
                </RequireAuth>
              }
            />
            <Route
              path="/dashboard/service-catalog"
              element={
                <RequireAuth>
                  <ServiceDashboard />
                </RequireAuth>
              }
            />
            <Route
              path="/dashboard/service-requests"
              element={
                <RequireAuth>
                  <ServiceRequests />
                </RequireAuth>
              }
            />
            <Route
              path="/dashboard/inquiries"
              element={<Navigate to="/dashboard/service-requests" replace />}
            />
            <Route
              path="/dashboard/leads"
              element={<Navigate to="/dashboard/service-requests" replace />}
            />
            <Route
              path="/dashboard/sms-notifications"
              element={
                <RequireAuth>
                  <SmsNotifications />
                </RequireAuth>
              }
            />
            <Route path="/verify-email" element={<EmailVerificationPage />} />
            <Route path="/storefront/:businessSlug" element={<StoreFrontPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordForm />} />
            <Route path="/reset-password" element={<ResetPasswordForm />} />
            <Route
              path="/ai-service-recommendations"
              element={<Recommendations />}
            />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </LaunchModeGate>
      </div >

      {/*The footer will appear on all components in the app */}
      < Footer />
      <Chatbot />
      {/* The Chatbot component will be available on all pages */}
    </ScrollToTop >
  );
};

const App = () => (
  <ThemeProvider theme={appTheme}>
    <Router>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </Router>
  </ThemeProvider>
);

export default App;